import React from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import { KeyboardArrowDown } from '@material-ui/icons';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import { useTheme } from '@material-ui/core/styles';

import { useStyles } from './styles';

export interface DropdownButtonOption {
  label: string;
  value?: string | number;
}

export interface DropdownButtonProps {
  options: DropdownButtonOption[];
  value: DropdownButtonOption['value'];
  onChange: (value: DropdownButtonOption['value']) => void;
}

export const DropdownButton: React.FC<DropdownButtonProps> = ({ options, onChange, value }) => {
  const classes = useStyles();
  const theme = useTheme();

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, option: DropdownButtonOption) => {
    onChange(option.value);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const selectedOption = React.useMemo(() => options.find(option => option.value === value), [options, value]);

  return (
    <Box className={classes.root}>
      <ButtonGroup
        variant="contained"
        color="primary"
        ref={anchorRef}
        aria-label="split button"
        className={classes.buttonGroup}
      >
        <Button
          variant="outlined"
          className={classes.button}
          color="primary"
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          {selectedOption?.label}
          <KeyboardArrowDown htmlColor={theme.palette.grey['600']} />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        className={classes.overlay}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList id="split-button-menu" className={classes.menu}>
                {options.map(option => (
                  <MenuItem
                    className={classes.menuItem}
                    key={option.value}
                    selected={option.value === value}
                    onClick={event => handleMenuItemClick(event, option)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};
